import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from './useAuth'

// ── HELPERS ──────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
function authorOf(prof) {
  if (!prof) return 'Anonymous'
  if (prof.full_name) return prof.full_name
  // Profile exists but full_name isn't set yet (e.g. older accounts /
  // Google OAuth users before the self-heal ran). Fall back to the email's
  // local-part so we still show *something* identifying instead of an
  // anonymising label.
  if (prof.email) return String(prof.email).split('@')[0]
  return 'Anonymous'
}
function avatarOf(prof) {
  return prof?.avatar_url || null
}

// ── VERSE COMMENTS ───────────────────────────────────────────
// All comments for the current user's church, grouped by verse_ref.
// The keyed-by-verse shape lets the UI read comments[vk] for any verse
// without doing per-verse fetches.

// `churchIdsOverride` — pass the user's home + followed church ids (e.g.
// BibleApp.jsx's `effectiveSermonChurchIds`) to read comments from followed
// churches too, per docs/FIX-BIBLE-COMMENTS.md ("and followed churches, if
// that setting is on"). Falls back to just the home church when omitted —
// existing callers are unaffected.
export function useVerseComments(churchIdsOverride) {
  const [commentsByVerse, setCommentsByVerse] = useState({})
  const [loading, setLoading] = useState(true)
  const { user, profile } = useAuth()
  const churchId = profile?.church_id
  const churchIds = (Array.isArray(churchIdsOverride) && churchIdsOverride.length > 0)
    ? churchIdsOverride
    : (churchId ? [churchId] : [])
  const churchIdsKey = churchIds.slice().sort().join(',')

  const fetch = useCallback(async () => {
    if (churchIds.length === 0) { setCommentsByVerse({}); setLoading(false); return }
    setLoading(true)
    const { data, error } = await supabase
      .from('verse_comments')
      .select(`*, author:profiles!author_id(full_name, avatar_url), verse_comment_likes(user_id)`)
      .in('church_id', churchIds)
      .eq('removed', false)
      .order('created_at', { ascending: true })
    if (error) { console.error('useVerseComments load:', error); setLoading(false); return }
    const shape = (c) => ({
      ...c,
      author:       authorOf(c.author),
      authorAvatar: avatarOf(c.author),
      authorId:     c.author_id,
      date:     formatDate(c.created_at),
      likes:    c.verse_comment_likes?.length || 0,
      liked:    c.verse_comment_likes?.some(l => l.user_id === user?.id) || false,
      private:  !!c.private,
      parentId: c.parent_id || null,
    })
    const all = (data || []).map(shape)
    const repliesBy = {}
    for (const c of all) if (c.parentId) (repliesBy[c.parentId] = repliesBy[c.parentId] || []).push(c)
    const byVerse = {}
    for (const c of all) {
      if (c.parentId) continue   // replies attach to their parent below
      const withReplies = { ...c, replies: (repliesBy[c.id] || []) }
      byVerse[c.verse_ref] = byVerse[c.verse_ref] || []
      byVerse[c.verse_ref].push(withReplies)
    }
    setCommentsByVerse(byVerse)
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchIdsKey, user?.id])

  useEffect(() => { fetch() }, [fetch])

  async function addComment(verseRef, text, isPrivate = false, parentId = null) {
    if (!user)        return { error: 'You need to be signed in.' }
    if (!churchId)    return { error: 'No church on your profile.' }
    if (!text?.trim()) return { error: 'Comment is empty.' }
    const m = verseRef.match(/^(.+)\s(\d+):(\d+)$/)
    if (!m) return { error: 'Bad verse reference.' }

    const base = {
      church_id: churchId,
      author_id: user.id,
      verse_ref: verseRef,
      book:      m[1],
      chapter:   parseInt(m[2]),
      verse:     parseInt(m[3]),
      text:      text.trim(),
      ...(parentId ? { parent_id: parentId } : {}),
    }
    const selectCols = `*, author:profiles!author_id(full_name, avatar_url), verse_comment_likes(user_id)`

    // Try with the private flag; retry without it if the column doesn't exist yet.
    let { data, error } = await supabase.from('verse_comments')
      .insert({ ...base, private: !!isPrivate }).select(selectCols).single()
    if (error && /column.*private.*does not exist/i.test(error.message)) {
      if (isPrivate) {
        return { error: 'Private notes need a database migration. Run: alter table public.verse_comments add column private boolean not null default false;' }
      }
      ;({ data, error } = await supabase.from('verse_comments').insert(base).select(selectCols).single())
    }
    // Tolerate the parent_id column not existing yet (migration not run).
    if (error && parentId && /parent_id/i.test(error.message || '')) {
      const { parent_id, ...noParent } = base
      ;({ data, error } = await supabase.from('verse_comments').insert({ ...noParent, private: !!isPrivate }).select(selectCols).single())
    }
    if (error) { console.error('addComment:', error); return { error: error.message } }

    // Optimistically prepend top-level comments (replies just wait for refetch).
    if (data && !parentId) {
      const shaped = {
        ...data,
        author:       authorOf(data.author),
        authorAvatar: avatarOf(data.author),
        authorId:     data.author_id,
        date:         formatDate(data.created_at),
        likes:        0,
        liked:        false,
        private:      !!data.private,
      }
      setCommentsByVerse(curr => ({
        ...curr,
        [data.verse_ref]: [...(curr[data.verse_ref] || []), shaped],
      }))
    }
    fetch()
    return { error: null }
  }

  async function toggleLike(commentId, currentlyLiked) {
    if (!user) return
    if (currentlyLiked) {
      await supabase.from('verse_comment_likes').delete()
        .eq('comment_id', commentId).eq('user_id', user.id)
    } else {
      await supabase.from('verse_comment_likes').insert({ comment_id: commentId, user_id: user.id })
    }
    fetch()
  }

  async function removeComment(commentId, text) {
    if (!user) return
    await supabase.from('verse_comments').update({ removed: true, removed_by: user.id, removed_at: new Date().toISOString() }).eq('id', commentId)
    await supabase.from('removed_posts').insert({ post_type: 'verse_comment', post_id: commentId, original_text: text, removed_by: user.id })
    fetch()
  }

  // Edit a private note's text (docs/FIX-SERMON-CLIPS.md §2a Comments tab).
  async function editComment(commentId, text) {
    if (!user || !text?.trim()) return { error: 'Comment is empty.' }
    const { error } = await supabase.from('verse_comments').update({ text: text.trim() }).eq('id', commentId)
    if (error) { console.error('editComment:', error); return { error: error.message } }
    fetch()
    return { error: null }
  }

  // "Share with [church]" — turns a private note public. One-directional;
  // there's no UI path back to private once shared.
  async function setPrivacy(commentId, isPrivate) {
    if (!user) return
    const { error } = await supabase.from('verse_comments').update({ private: !!isPrivate }).eq('id', commentId)
    if (error) { console.error('setPrivacy:', error); return { error: error.message } }
    fetch()
    return { error: null }
  }

  return { commentsByVerse, loading, addComment, toggleLike, removeComment, editComment, setPrivacy, refresh: fetch }
}

// ── FLAGGED VERSES ───────────────────────────────────────────

export function useFlaggedVerses() {
  const [flagged, setFlagged] = useState([])
  const [themes,  setThemes]  = useState([])
  const { user, isElder } = useAuth()

  useEffect(() => {
    fetchFlagged()
    if (isElder) fetchThemes()
  }, [isElder])

  async function fetchFlagged() {
    const { data } = await supabase.from('flagged_verses').select('*').eq('user_id', user?.id)
    setFlagged(data || [])
  }

  async function fetchThemes() {
    const { data } = await supabase.from('flagged_themes').select('*')
    setThemes(data || [])
  }

  async function toggleFlag(verseRef, book, chapter, verse, verseText) {
    const existing = flagged.find(f => f.verse_ref === verseRef)
    if (existing) {
      await supabase.from('flagged_verses').delete().eq('id', existing.id)
    } else {
      await supabase.from('flagged_verses').insert({ user_id: user.id, verse_ref: verseRef, book, chapter, verse, verse_text: verseText })
    }
    fetchFlagged()
    if (isElder) fetchThemes()
  }

  const isFlagged = (ref) => flagged.some(f => f.verse_ref === ref)
  return { flagged, themes, toggleFlag, isFlagged }
}

// ── ALL FLAGGED (elder view) ──────────────────────────────────

export function useAllFlagged() {
  const [allFlagged, setAllFlagged] = useState([])
  const [themes, setThemes] = useState([])

  useEffect(() => {
    supabase.from('flagged_verses').select('*, profiles(full_name)').order('created_at', { ascending: false })
      .then(({ data }) => setAllFlagged(data || []))
    supabase.from('flagged_themes').select('*')
      .then(({ data }) => setThemes(data || []))
  }, [])

  return { allFlagged, themes }
}

// ── PRAYERS ───────────────────────────────────────────────────

export function usePrayers() {
  const [prayers, setPrayers] = useState([])
  const [loading, setLoading] = useState(true)
  const { user, profile } = useAuth()
  const churchId = profile?.church_id

  const fetch = useCallback(async () => {
    if (!churchId) { setPrayers([]); setLoading(false); return }
    setLoading(true)
    const { data, error } = await supabase
      .from('prayers')
      .select(`*, author:profiles!author_id(full_name, avatar_url), prayer_reactions(user_id), prayer_comments(id, text, removed, created_at, author_id, author:profiles!author_id(full_name, avatar_url), prayer_comment_likes(user_id))`)
      .eq('church_id', churchId)
      .eq('removed', false)
      .order('created_at', { ascending: false })
    if (error) { console.error('usePrayers load:', error); setLoading(false); return }
    setPrayers((data || []).map(p => ({
      ...p,
      author:       authorOf(p.author),
      authorAvatar: avatarOf(p.author),
      authorId:     p.author_id,
      date:         formatDate(p.created_at),
      createdAt:    p.created_at,
      visibility:   p.visibility || 'church',
      prayCount:    p.prayer_reactions?.length || 0,
      prayed:       p.prayer_reactions?.some(r => r.user_id === user?.id) || false,
      comments: (p.prayer_comments || [])
        .filter(c => !c.removed)
        .map(c => ({
          ...c,
          author:       authorOf(c.author),
          authorAvatar: avatarOf(c.author),
          authorId:     c.author_id,
          date:         formatDate(c.created_at),
          likes:        c.prayer_comment_likes?.length || 0,
          liked:        c.prayer_comment_likes?.some(l => l.user_id === user?.id) || false,
        }))
    })))
    setLoading(false)
  }, [churchId, user?.id])

  useEffect(() => { fetch() }, [fetch])

  // Accepts (text, visibility?) where visibility is 'church' (default) or
  // 'elders'. Tolerates the visibility column not existing yet - falls back
  // to an insert without it so users on un-migrated installs still post.
  async function addPrayer(text, visibility = 'church') {
    if (!user || !churchId || !text?.trim()) return
    const base = { church_id: churchId, author_id: user.id, text: text.trim() }
    let res = await supabase.from('prayers')
      .insert({ ...base, visibility })
      .select(`*, author:profiles!author_id(full_name, avatar_url)`).single()
    if (res.error) {
      console.warn('addPrayer with visibility failed, retrying without:', res.error.message)
      res = await supabase.from('prayers').insert(base)
        .select(`*, author:profiles!author_id(full_name, avatar_url)`).single()
    }
    const { data, error } = res
    if (error) { console.error('addPrayer:', error); return }
    // Optimistically prepend so the new prayer appears immediately.
    if (data) {
      setPrayers(curr => [{
        ...data,
        author:       authorOf(data.author),
        authorAvatar: avatarOf(data.author),
        authorId:     data.author_id,
        date:         formatDate(data.created_at),
        visibility:   data.visibility || visibility,
        prayCount:    0,
        prayed:       false,
        comments:     [],
      }, ...curr])
    }
    fetch()
  }

  async function togglePray(prayerId, currentlyPrayed) {
    if (!user) return
    if (currentlyPrayed) {
      await supabase.from('prayer_reactions').delete().eq('prayer_id', prayerId).eq('user_id', user.id)
    } else {
      await supabase.from('prayer_reactions').insert({ prayer_id: prayerId, user_id: user.id })
    }
    fetch()
  }

  async function addComment(prayerId, text) {
    if (!user || !text?.trim()) return
    const { data, error } = await supabase.from('prayer_comments')
      .insert({ prayer_id: prayerId, author_id: user.id, text: text.trim() })
      .select(`*, author:profiles!author_id(full_name, avatar_url)`).single()
    if (error) { console.error('addPrayerComment:', error); return }
    if (data) {
      const shaped = {
        ...data,
        author:       authorOf(data.author),
        authorAvatar: avatarOf(data.author),
        authorId:     data.author_id,
        date:         formatDate(data.created_at),
        likes:        0,
        liked:        false,
      }
      setPrayers(curr => curr.map(p =>
        p.id === prayerId ? { ...p, comments: [...p.comments, shaped] } : p
      ))
    }
    fetch()
  }

  async function likePrayerComment(commentId, liked) {
    if (!user) return
    if (liked) {
      await supabase.from('prayer_comment_likes').delete().eq('comment_id', commentId).eq('user_id', user.id)
    } else {
      await supabase.from('prayer_comment_likes').insert({ comment_id: commentId, user_id: user.id })
    }
    fetch()
  }

  async function removePrayer(prayerId, text) {
    if (!user) return
    await supabase.from('prayers').update({ removed: true, removed_by: user.id, removed_at: new Date().toISOString() }).eq('id', prayerId)
    await supabase.from('removed_posts').insert({ post_type: 'prayer', post_id: prayerId, original_text: text, removed_by: user.id })
    fetch()
  }

  async function removePrayerComment(commentId, text) {
    if (!user) return
    await supabase.from('prayer_comments').update({ removed: true, removed_by: user.id, removed_at: new Date().toISOString() }).eq('id', commentId)
    await supabase.from('removed_posts').insert({ post_type: 'prayer_comment', post_id: commentId, original_text: text, removed_by: user.id })
    fetch()
  }

  return { prayers, loading, addPrayer, togglePray, addComment, likePrayerComment, removePrayer, removePrayerComment, refresh: fetch }
}

// ── ENCOURAGEMENTS ───────────────────────────────────────────

export function useEncouragements(churchIdOverride) {
  const [encouragements, setEncouragements] = useState([])
  const [loading, setLoading] = useState(true)
  const { user, profile } = useAuth()
  // Super admins don't have a `church_id` on their profile - they pick one
  // via `activeChurchId` in the app. Accept that as an override so the feed
  // shows the right church's encouragements regardless of role.
  const churchId = churchIdOverride || profile?.church_id

  const fetch = useCallback(async () => {
    if (!churchId) { setEncouragements([]); setLoading(false); return }
    setLoading(true)
    // Try the full select (includes encouragement_refs and new columns).
    // If the encouragement_refs table or columns don't exist yet (migration
    // not run), retry without the join so the feed still renders.
    const fullSel = `*, author:profiles!author_id(full_name, avatar_url), encouragement_likes(user_id), encouragement_refs(*)`
    const slimSel = `*, author:profiles!author_id(full_name, avatar_url), encouragement_likes(user_id)`
    let res = await supabase
      .from('encouragements').select(fullSel)
      .eq('church_id', churchId).eq('removed', false)
      .order('created_at', { ascending: false })
    if (res.error) {
      console.warn('Encouragements full select failed - retrying without refs join:', res.error.message)
      res = await supabase
        .from('encouragements').select(slimSel)
        .eq('church_id', churchId).eq('removed', false)
        .order('created_at', { ascending: false })
    }
    const { data, error } = res
    if (error) { console.error('useEncouragements load:', error); setLoading(false); return }

    const rows = data || []
    // If the relational join returned nothing (either we fell back to slim or
    // PostgREST's schema cache hadn't picked up the FK yet), do a direct
    // select on encouragement_refs so verse-to-encouragement linking still
    // works. Cheap second roundtrip - one query, all refs.
    let refsByEnc = new Map()
    const haveRefsFromJoin = rows.some(r => Array.isArray(r.encouragement_refs) && r.encouragement_refs.length > 0)
    if (rows.length > 0 && !haveRefsFromJoin) {
      const ids = rows.map(r => r.id)
      const refsRes = await supabase
        .from('encouragement_refs').select('*').in('encouragement_id', ids)
      if (refsRes.error) {
        console.warn('encouragement_refs fallback select failed:', refsRes.error.message)
      } else {
        for (const r of refsRes.data || []) {
          if (!refsByEnc.has(r.encouragement_id)) refsByEnc.set(r.encouragement_id, [])
          refsByEnc.get(r.encouragement_id).push(r)
        }
      }
    }

    // Comments (separate query so a missing encouragement_comments table never
    // breaks the feed). Nests one level of replies + comment likes.
    let commentsByEnc = new Map()
    if (rows.length > 0) {
      const ids = rows.map(r => r.id)
      const cRes = await supabase
        .from('encouragement_comments')
        .select('*, author:profiles!author_id(full_name, avatar_url), encouragement_comment_likes(user_id)')
        .in('encouragement_id', ids).eq('removed', false)
        .order('created_at', { ascending: true })
      if (cRes.error) {
        console.warn('encouragement_comments select failed (migration not run?):', cRes.error.message)
      } else {
        const shapeC = (c) => ({
          id: c.id, encId: c.encouragement_id, parentId: c.parent_id || null,
          author: authorOf(c.author), authorAvatar: avatarOf(c.author), authorId: c.author_id,
          date: formatDate(c.created_at), createdAt: c.created_at, text: c.text,
          likes: (c.encouragement_comment_likes || []).length,
          liked: (c.encouragement_comment_likes || []).some(l => l.user_id === user?.id),
        })
        const all = (cRes.data || []).map(shapeC)
        const repliesBy = {}
        for (const c of all) if (c.parentId) (repliesBy[c.parentId] = repliesBy[c.parentId] || []).push(c)
        for (const c of all) {
          if (c.parentId) continue
          if (!commentsByEnc.has(c.encId)) commentsByEnc.set(c.encId, [])
          commentsByEnc.get(c.encId).push({ ...c, replies: (repliesBy[c.id] || []) })
        }
      }
    }

    setEncouragements(rows.map(e => {
      const joined = Array.isArray(e.encouragement_refs) && e.encouragement_refs.length > 0
        ? e.encouragement_refs
        : (refsByEnc.get(e.id) || [])
      const eComments = commentsByEnc.get(e.id) || []
      return {
        ...e,
        author:       authorOf(e.author),
        authorAvatar: avatarOf(e.author),
        authorId:     e.author_id,
        title:        e.title || '',
        // `name` is the short AI-generated title (5 words max) introduced
        // alongside the full-transcript `text`. Falls back to `title` for
        // pre-migration rows that only have the verse-ref title.
        name:         e.name || '',
        summary:      e.summary || '',
        sharedBy:     e.shared_by || '',
        eventDate: e.event_date || null,
        date:      formatDate(e.event_date || e.created_at),
        createdAt: e.created_at,
        link:      e.link_url || '',
        linkLabel: e.link_label || '',
        refs:      joined.map(r => ({
          id:        r.id,
          verseRef:  r.verse_ref,
          book:      r.book,
          chapter:   r.chapter,
          verse:     r.verse,
          timestamp: r.timestamp,
          notes:     r.notes,
        })),
        likes:     e.encouragement_likes?.length || 0,
        liked:     e.encouragement_likes?.some(l => l.user_id === user?.id) || false,
        comments:  eComments,
        commentCount: eComments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0),
      }
    }))
    setLoading(false)
  }, [churchId, user?.id])

  useEffect(() => { fetch() }, [fetch])

  // Accepts either the legacy positional signature (text, linkUrl, linkLabel)
  // or an options object: { text, sharedBy, eventDate, linkUrl, linkLabel, refs, churchId }.
  async function addEncouragement(arg1, arg2, arg3) {
    const opts = (arg1 && typeof arg1 === 'object')
      ? arg1
      : { text: arg1, linkUrl: arg2, linkLabel: arg3 }

    const text       = opts.text?.trim()
    const targetCid  = opts.churchId || churchId
    if (!user)      return { error: 'You need to be signed in.' }
    if (!targetCid) return { error: 'No active church - pick one in your profile first.' }
    if (!text)      return { error: 'Encouragement is empty.' }

    const base = {
      church_id:  targetCid,
      author_id:  user.id,
      text,
      link_url:   opts.linkUrl?.trim()   || null,
      link_label: opts.linkLabel?.trim() || null,
    }
    const extras = {
      shared_by:          opts.sharedBy?.trim() || null,
      event_date:         opts.eventDate        || null,
      title:              opts.title?.trim()    || null,
      summary:            opts.summary?.trim()  || null,
      start_ms:           opts.startMs ?? null,
      end_ms:             opts.endMs ?? null,
      transcript_excerpt: opts.transcriptExcerpt?.trim() || null,
    }
    const selectCols = `*, author:profiles!author_id(full_name, avatar_url)`

    // Try with the new columns; if anything errors (column missing / RLS /
    // anything), retry once with just the base shape so a stale schema can't
    // silently swallow the save.
    let res = await supabase
      .from('encouragements').insert({ ...base, ...extras })
      .select(selectCols).single()
    if (res.error) {
      console.warn('Encouragement insert failed - retrying with base columns only:', res.error.message)
      res = await supabase
        .from('encouragements').insert(base)
        .select(selectCols).single()
    }
    const { data, error } = res
    if (error) {
      console.error('addEncouragement final error:', error)
      return { error: error.message || String(error) }
    }

    // Insert refs if any were detected by the scanner. Tolerate the table not
    // existing - we don't want to lose the parent encouragement just because
    // the refs migration hasn't run yet.
    const refs = Array.isArray(opts.refs) ? opts.refs : []
    if (refs.length > 0 && data?.id) {
      const baseRefRows = refs.map(r => ({
        encouragement_id: data.id,
        verse_ref:        r.verseRef,
        book:             r.book,
        chapter:          r.chapter,
        verse:            r.verse,
        end_verse:        r.endVerse ?? r.end_verse ?? r.verse ?? null,
        timestamp_s:      r.timestampS ?? null,
        timestamp:        r.timestamp  || null,
        notes:            r.notes      || null,
      }))
      let { error: rErr } = await supabase.from('encouragement_refs').insert(baseRefRows)
      if (rErr && /column.*end_verse.*does not exist/i.test(rErr.message || '')) {
        // Older schema without the end_verse column — strip and retry.
        ;({ error: rErr } = await supabase.from('encouragement_refs').insert(
          // eslint-disable-next-line no-unused-vars
          baseRefRows.map(({ end_verse, ...rest }) => rest)
        ))
      }
      if (rErr) console.warn('addEncouragement refs (skipped):', rErr.message)
    }

    fetch()
    return { error: null, id: data?.id }
  }

  async function toggleLike(id, currentlyLiked) {
    if (!user) return { error: 'Sign in to like.' }
    // Optimistic local update so the pill flips immediately. If the DB
    // call fails we re-fetch to revert.
    setEncouragements(curr => curr.map(e => e.id !== id ? e : ({
      ...e,
      liked: !currentlyLiked,
      likes: Math.max(0, (e.likes || 0) + (currentlyLiked ? -1 : 1)),
    })))
    if (currentlyLiked) {
      const { error } = await supabase.from('encouragement_likes')
        .delete().eq('encouragement_id', id).eq('user_id', user.id)
      if (error) { console.warn('toggleLike unlike (encouragement):', error.message); fetch(); return { error: error.message } }
    } else {
      const { error } = await supabase.from('encouragement_likes')
        .insert({ encouragement_id: id, user_id: user.id })
      if (error) { console.warn('toggleLike like (encouragement):', error.message); fetch(); return { error: error.message } }
    }
    return { error: null }
  }

  async function removeEncouragement(id, text) {
    if (!user) return
    await supabase.from('encouragements').update({ removed: true, removed_by: user.id, removed_at: new Date().toISOString() }).eq('id', id)
    await supabase.from('removed_posts').insert({ post_type: 'encouragement', post_id: id, original_text: text, removed_by: user.id })
    fetch()
  }

  // Moderator edit. Accepts the same `opts` shape as addEncouragement
  // and overwrites the editable fields on an existing row. Also replaces
  // the verse refs if `refs` is provided.
  async function updateEncouragement(id, opts = {}) {
    if (!user) return { error: 'Sign in first.' }
    if (!id)   return { error: 'No encouragement id.' }
    const updates = {
      text:               (opts.text || '').trim(),
      title:              opts.title?.trim()     || null,
      shared_by:          opts.sharedBy?.trim()  || null,
      event_date:         opts.eventDate         || null,
      link_url:           opts.linkUrl?.trim()   || null,
      link_label:         opts.linkLabel?.trim() || null,
      summary:            opts.summary?.trim()   || null,
      start_ms:           opts.startMs ?? null,
      end_ms:             opts.endMs ?? null,
      transcript_excerpt: opts.transcriptExcerpt?.trim() || null,
    }
    if (!updates.text) return { error: 'Encouragement is empty.' }
    // Try with every editable column; retry progressively stripping newer
    // columns a stale schema might not have yet (summary/start_ms/end_ms/
    // transcript_excerpt, then title) rather than failing the whole save.
    // Supabase phrases a missing-column failure two ways depending on layer:
    //   - "column 'x' does not exist" (Postgres)
    //   - "Could not find the 'x' column ... in the schema cache" (PostgREST)
    const isMissingColumnErr = (e, col) => {
      const m = (e?.message || '').toLowerCase()
      return new RegExp(`${col}.*does not exist`).test(m) || new RegExp(`could not find.*${col}.*column`).test(m) || new RegExp(`${col}.*column.*schema cache`).test(m)
    }
    let res = await supabase.from('encouragements').update(updates).eq('id', id)
    if (res.error && ['summary', 'start_ms', 'end_ms', 'transcript_excerpt'].some(c => isMissingColumnErr(res.error, c))) {
      const { summary: _s, start_ms: _sm, end_ms: _em, transcript_excerpt: _te, ...rest } = updates
      res = await supabase.from('encouragements').update(rest).eq('id', id)
    }
    if (res.error && isMissingColumnErr(res.error, 'title')) {
      const { title: _omit, ...rest } = updates
      res = await supabase.from('encouragements').update(rest).eq('id', id)
    }
    if (res.error) return { error: res.error.message || String(res.error) }
    // Replace refs wholesale if the caller passed any.
    if (Array.isArray(opts.refs)) {
      await supabase.from('encouragement_refs').delete().eq('encouragement_id', id)
      if (opts.refs.length > 0) {
        const baseRefRows = opts.refs.map(r => ({
          encouragement_id: id,
          verse_ref:        r.verseRef,
          book:             r.book,
          chapter:          r.chapter,
          verse:            r.verse,
          end_verse:        r.endVerse ?? r.end_verse ?? r.verse ?? null,
          timestamp_s:      r.timestampS ?? null,
          timestamp:        r.timestamp  || null,
          notes:            r.notes      || null,
        }))
        let { error: rErr } = await supabase.from('encouragement_refs').insert(baseRefRows)
        if (rErr && /column.*end_verse.*does not exist/i.test(rErr.message || '')) {
          ;({ error: rErr } = await supabase.from('encouragement_refs').insert(
            // eslint-disable-next-line no-unused-vars
            baseRefRows.map(({ end_verse, ...rest }) => rest)
          ))
        }
        if (rErr) console.warn('updateEncouragement refs:', rErr.message)
      }
    }
    fetch()
    return { error: null }
  }

  async function addComment(encouragementId, text, parentId = null) {
    if (!user)     return { error: 'You need to be signed in.' }
    if (!churchId) return { error: 'No active church.' }
    const clean = (text || '').trim()
    if (!clean)    return { error: 'Comment is empty.' }
    const row = { encouragement_id: encouragementId, church_id: churchId, author_id: user.id, text: clean }
    if (parentId) row.parent_id = parentId
    let { error } = await supabase.from('encouragement_comments').insert(row)
    if (error && parentId && /parent_id/i.test(error.message || '')) {
      ;({ error } = await supabase.from('encouragement_comments').insert({ encouragement_id: encouragementId, church_id: churchId, author_id: user.id, text: clean }))
    }
    if (error) return { error: error.message || String(error) }
    fetch()
    return { error: null }
  }
  async function likeComment(commentId, currentlyLiked) {
    if (!user) return { error: 'Sign in to like.' }
    if (currentlyLiked) {
      const { error } = await supabase.from('encouragement_comment_likes').delete().eq('comment_id', commentId).eq('user_id', user.id)
      if (error) { console.warn('likeComment unlike (enc):', error.message); return { error: error.message } }
    } else {
      const { error } = await supabase.from('encouragement_comment_likes').insert({ comment_id: commentId, user_id: user.id, church_id: churchId })
      if (error) { console.warn('likeComment like (enc):', error.message); return { error: error.message } }
    }
    fetch()
    return { error: null }
  }
  async function removeComment(commentId) {
    if (!user) return { error: 'You need to be signed in.' }
    const { error } = await supabase.from('encouragement_comments')
      .update({ removed: true, removed_by: user.id, removed_at: new Date().toISOString() }).eq('id', commentId)
    if (error) return { error: error.message || String(error) }
    fetch()
    return { error: null }
  }

  return { encouragements, loading, addEncouragement, updateEncouragement, toggleLike, removeEncouragement, addComment, likeComment, removeComment, refresh: fetch }
}

// ── SAVED VERSES ─────────────────────────────────────────────

export function useSavedVerses() {
  const [saved, setSaved] = useState([])
  const { user } = useAuth()

  useEffect(() => {
    if (!user) return
    supabase.from('saved_verses').select('*').eq('user_id', user.id).order('created_at', { ascending: false })
      .then(({ data }) => setSaved(data || []))
  }, [user])

  async function toggleSave(verseRef, book, chapter, verse, verseText) {
    const existing = saved.find(s => s.verse_ref === verseRef)
    if (existing) {
      await supabase.from('saved_verses').delete().eq('id', existing.id)
      setSaved(s => s.filter(v => v.id !== existing.id))
    } else {
      const { data } = await supabase.from('saved_verses').insert({
        user_id: user.id, verse_ref: verseRef, book, chapter, verse, verse_text: verseText, note: ''
      }).select().single()
      if (data) setSaved(s => [data, ...s])
    }
  }

  async function updateNote(id, note) {
    await supabase.from('saved_verses').update({ note }).eq('id', id)
    setSaved(s => s.map(v => v.id === id ? { ...v, note } : v))
  }

  const isSaved = (ref) => saved.some(s => s.verse_ref === ref)
  return { saved, toggleSave, updateNote, isSaved }
}

// ── HIGHLIGHTS ───────────────────────────────────────────────

export function useHighlights() {
  const [highlights, setHighlights] = useState([])
  const { user } = useAuth()

  useEffect(() => {
    if (!user) return
    supabase.from('highlights').select('*').eq('user_id', user.id)
      .then(({ data }) => setHighlights(data || []))
  }, [user])

  async function toggleHighlight(verseRef, color) {
    const existing = highlights.find(h => h.verse_ref === verseRef)
    if (existing) {
      if (existing.color === color) {
        await supabase.from('highlights').delete().eq('id', existing.id)
        setHighlights(h => h.filter(v => v.id !== existing.id))
      } else {
        const { data } = await supabase.from('highlights').update({ color }).eq('id', existing.id).select().single()
        if (data) setHighlights(h => h.map(v => v.id === existing.id ? data : v))
      }
    } else {
      const { data } = await supabase.from('highlights').insert({ user_id: user.id, verse_ref: verseRef, color }).select().single()
      if (data) setHighlights(h => [...h, data])
    }
  }

  const getColor = (ref) => highlights.find(h => h.verse_ref === ref)?.color || null
  return { highlights, toggleHighlight, getColor }
}

// ── COMMUNITY BOARD ──────────────────────────────────────────
// Church-scoped posts + comments. Members and moderators can post and
// comment; visitors are blocked at the RLS layer.

export function useCommunityBoard(churchIdOverride) {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const { user, profile } = useAuth()
  const churchId = churchIdOverride || profile?.church_id

  const fetch = useCallback(async () => {
    if (!churchId) { setPosts([]); setLoading(false); return }
    setLoading(true)
    const cFull = `comments:community_post_comments(*, author:profiles!author_id(full_name, avatar_url), community_post_comment_likes(user_id))`
    const cSlim = `comments:community_post_comments(*, author:profiles!author_id(full_name, avatar_url))`
    // Progressive fallbacks: comment-likes table missing → drop it; post-likes
    // table missing → drop that too.
    const variants = [
      `*, author:profiles!author_id(full_name, avatar_url), ${cFull}, community_post_likes(user_id)`,
      `*, author:profiles!author_id(full_name, avatar_url), ${cSlim}, community_post_likes(user_id)`,
      `*, author:profiles!author_id(full_name, avatar_url), ${cSlim}`,
    ]
    let res
    for (const sel of variants) {
      res = await supabase.from('community_posts').select(sel)
        .eq('church_id', churchId).eq('removed', false)
        .order('created_at', { ascending: false })
      if (!res.error) break
      console.warn('useCommunityBoard load variant failed - retrying simpler:', res.error.message)
    }
    const { data, error } = res
    if (error) {
      console.error('useCommunityBoard load:', error)
      setPosts([])
      setLoading(false)
      return
    }
    const rows = (data || []).map(p => ({
      id:           p.id,
      author:       authorOf(p.author),
      authorAvatar: avatarOf(p.author),
      authorId:     p.author_id,
      date:         formatDate(p.created_at),
      createdAt:    p.created_at,
      text:         p.text,
      likes:        (p.community_post_likes || []).length,
      liked:        (p.community_post_likes || []).some(l => l.user_id === user?.id),
      comments:     (() => {
        const shape = (c) => ({
          id:           c.id,
          author:       authorOf(c.author),
          authorAvatar: avatarOf(c.author),
          authorId:     c.author_id,
          date:         formatDate(c.created_at),
          createdAt:    c.created_at,
          text:         c.text,
          parentId:     c.parent_id || null,
          likes:        (c.community_post_comment_likes || []).length,
          liked:        (c.community_post_comment_likes || []).some(l => l.user_id === user?.id),
        })
        const byTime = (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        const all = (p.comments || []).filter(c => !c.removed).map(shape)
        const repliesBy = {}
        for (const c of all) if (c.parentId) (repliesBy[c.parentId] = repliesBy[c.parentId] || []).push(c)
        // Top-level comments with their (one level of) replies attached.
        return all.filter(c => !c.parentId).sort(byTime)
          .map(c => ({ ...c, replies: (repliesBy[c.id] || []).sort(byTime) }))
      })(),
    }))
    setPosts(rows)
    setLoading(false)
  }, [churchId, user?.id])

  async function toggleLike(postId, currentlyLiked) {
    if (!user)     return { error: 'Sign in to like.' }
    if (!churchId) return { error: 'No active church.' }
    // Optimistic local update — DB call below catches any failure.
    setPosts(curr => curr.map(p => p.id !== postId ? p : ({
      ...p,
      liked: !currentlyLiked,
      likes: Math.max(0, (p.likes || 0) + (currentlyLiked ? -1 : 1)),
    })))
    if (currentlyLiked) {
      const { error } = await supabase.from('community_post_likes')
        .delete().eq('post_id', postId).eq('user_id', user.id)
      if (error) { console.warn('toggleLike unlike (community post):', error.message); fetch(); return { error: error.message } }
    } else {
      const { error } = await supabase.from('community_post_likes')
        .insert({ post_id: postId, user_id: user.id, church_id: churchId })
      if (error) { console.warn('toggleLike like (community post):', error.message); fetch(); return { error: error.message } }
    }
    return { error: null }
  }

  useEffect(() => { fetch() }, [fetch])

  async function addPost(text) {
    if (!user)      return { error: 'You need to be signed in.' }
    if (!churchId)  return { error: 'No active church.' }
    const clean = (text || '').trim()
    if (!clean)     return { error: 'Post is empty.' }
    const { error } = await supabase.from('community_posts').insert({
      church_id: churchId, author_id: user.id, text: clean,
    })
    if (error) return { error: error.message || String(error) }
    fetch()
    return { error: null }
  }

  async function addComment(postId, text, parentId = null) {
    if (!user)      return { error: 'You need to be signed in.' }
    if (!churchId)  return { error: 'No active church.' }
    const clean = (text || '').trim()
    if (!clean)     return { error: 'Comment is empty.' }
    const row = { post_id: postId, church_id: churchId, author_id: user.id, text: clean }
    if (parentId) row.parent_id = parentId
    let { error } = await supabase.from('community_post_comments').insert(row)
    // Tolerate the parent_id column not existing yet (migration not run).
    if (error && parentId && /parent_id/i.test(error.message || '')) {
      ;({ error } = await supabase.from('community_post_comments').insert({ post_id: postId, church_id: churchId, author_id: user.id, text: clean }))
    }
    if (error) return { error: error.message || String(error) }
    fetch()
    return { error: null }
  }

  async function likeComment(commentId, currentlyLiked) {
    if (!user)     return { error: 'Sign in to like.' }
    if (currentlyLiked) {
      const { error } = await supabase.from('community_post_comment_likes')
        .delete().eq('comment_id', commentId).eq('user_id', user.id)
      if (error) { console.warn('likeComment unlike (board):', error.message); return { error: error.message } }
    } else {
      const { error } = await supabase.from('community_post_comment_likes')
        .insert({ comment_id: commentId, user_id: user.id, church_id: churchId })
      if (error) { console.warn('likeComment like (board):', error.message); return { error: error.message } }
    }
    fetch()
    return { error: null }
  }

  async function removePost(postId) {
    if (!user) return { error: 'You need to be signed in.' }
    const target = posts.find(p => p.id === postId)
    const { error } = await supabase.from('community_posts')
      .update({ removed: true }).eq('id', postId)
    if (error) return { error: error.message || String(error) }
    // Log to removed_posts so the Removed Posts panel + restore work.
    await supabase.from('removed_posts').insert({
      post_type: 'community_post', post_id: postId,
      original_text: target?.text || '', removed_by: user.id,
    })
    setPosts(curr => curr.filter(p => p.id !== postId))
    return { error: null }
  }

  return { posts, loading, addPost, addComment, likeComment, removePost, toggleLike, refresh: fetch }
}

// ── NOTIFICATIONS ────────────────────────────────────────────
// Inbox driven by SECURITY DEFINER triggers in the DB. RLS limits
// every read/update to the recipient's own rows. Polled every 60s.

export function useNotifications() {
  const [items,       setItems]       = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const { user } = useAuth()

  const fetch = useCallback(async () => {
    if (!user?.id) { setItems([]); setUnreadCount(0); return }
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .limit(200)
    if (error) { console.warn('useNotifications load:', error.message); return }
    setItems(data || [])
    setUnreadCount((data || []).filter(n => !n.read).length)
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    fetch()
    const id = setInterval(fetch, 60000)
    return () => clearInterval(id)
  }, [user?.id, fetch])

  async function markAsRead(notificationId) {
    if (!user?.id) return
    const { error } = await supabase
      .from('notifications').update({ read: true })
      .eq('id', notificationId)
      .eq('recipient_id', user.id)
    if (error) { console.warn('markAsRead:', error.message); return }
    setItems(curr => curr.map(n => n.id === notificationId ? { ...n, read: true } : n))
    setUnreadCount(c => Math.max(0, c - 1))
  }

  async function markAllRead() {
    if (!user?.id) return
    const { error } = await supabase
      .from('notifications').update({ read: true })
      .eq('recipient_id', user.id)
      .eq('read', false)
    if (error) { console.warn('markAllRead:', error.message); return }
    setItems(curr => curr.map(n => ({ ...n, read: true })))
    setUnreadCount(0)
  }

  return { items, unreadCount, refresh: fetch, markAsRead, markAllRead }
}

// ── REMOVED POSTS (elder/admin) ──────────────────────────────

export function useRemovedPosts() {
  const [posts, setPosts] = useState([])

  useEffect(() => {
    supabase.from('removed_posts')
      .select('*, profiles!removed_by(full_name)')
      .order('removed_at', { ascending: false })
      .then(({ data }) => setPosts(data || []))
  }, [])

  return { posts }
}

// ── INVITATIONS (elder/admin) ────────────────────────────────

export function useInvitations() {
  const [invitations, setInvitations] = useState([])
  const { user, sendInvite } = useAuth()

  useEffect(() => {
    if (!user) return
    supabase.from('invitations').select('*').eq('invited_by', user.id).order('created_at', { ascending: false })
      .then(({ data }) => setInvitations(data || []))
  }, [user])

  async function invite(emailOrPhone, role) {
    const { data, error } = await sendInvite(emailOrPhone, role)
    if (data) setInvitations(i => [data, ...i])
    return { data, error }
  }

  return { invitations, invite }
}
